import {
  useContext,
  useState,
  useEffect,
  useMemo,
} from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import Box from '@mui/material/Box';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Chip from '@mui/material/Chip';
import DoneIcon from '@mui/icons-material/Done';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import InputAdornment from '@mui/material/InputAdornment';
import { TRANSACTION_ITEM_TYPES, CHARGE_TYPE, COUPON_TARGET_TYPES } from '#common/constants';

import {
  DATE_FORMAT_STRING,
  formatCurrency,
  calcItemPaidAmounts,
} from '#common/utils';
import {
  transactionValidations,
} from '#common/validation';
import useForm from '#utils/use-form';
import { FeedbackUIContext } from '#context/feedback-ui-context';

interface OrderItem {
  id: string | number;
  unitPrice: number;
  amounts: number;
  productId?: string | number;
  product: { id?: string | number; name?: string; collectionIds?: Array<string | number> };
  color?: string;
  size?: string;
  [key: string]: unknown;
}

interface AppliedCoupon {
  targetType?: string | number;
  targetIds?: Array<string | number>;
  discount: number;
}

interface OrderShape {
  items: OrderItem[];
  appliedCoupons?: AppliedCoupon[];
  [key: string]: unknown;
}

interface AdditionalCharge {
  _id: string | number;
  name: string;
  price: number;
  [key: string]: unknown;
}

interface TransactionItem {
  type: string | number;
  itemId?: string | number;
  value?: string | number;
  amount: number;
}

interface Transaction {
  items?: TransactionItem[];
  [key: string]: unknown;
}

interface CalculationBreakdown {
  requested: number;
  actual: number;
  wasCapped: boolean;
}

interface TransactionFormValues {
  transactionDate: string | Date;
  paymentMethod: string;
  notes: string;
  chargeType: string | number;
  chargeValue: number | string;
  amount?: number;
}

interface TransactionPanelProps {
  transaction?: Transaction | undefined;
  transactions?: Transaction[] | undefined;
  order: OrderShape;
  additionalCharges: AdditionalCharge[];
  onSaveTransaction: (values: Record<string, unknown>) => void;
}

/**
 * Distribute applied coupon discounts across order items to get each item's
 * effective (post-discount) price. Handles CART, PRODUCT, and COLLECTION targets.
 * Each coupon's total discount is split proportionally by gross item weight
 * among the items it targets.
 */
function buildEffectivePriceMap(items: OrderItem[], appliedCoupons?: AppliedCoupon[]): Record<string, number> {
  const map: Record<string, number> = {};
  items.forEach((item) => {
    map[item.id] = item.unitPrice * item.amounts;
  });

  if (!appliedCoupons || appliedCoupons.length === 0) return map;

  // The true target: gross items total minus every coupon's recorded discount.
  // sum(coupon.discount) === order.couponDiscount because the server stores the
  // already-capped amount per coupon. This is used below to correct for overflow.
  const grossTotal = Object.values(map).reduce((sum, v) => sum + v, 0);
  const totalCouponDiscount = appliedCoupons.reduce((sum, c) => sum + (c.discount || 0), 0);
  const targetSum = Math.max(0, grossTotal - totalCouponDiscount);

  appliedCoupons.forEach((coupon) => {
    let matchingItems: OrderItem[];

    if (coupon.targetType === COUPON_TARGET_TYPES.CART) {
      matchingItems = items;
    } else if (coupon.targetType === COUPON_TARGET_TYPES.PRODUCT) {
      matchingItems = items.filter(
        (item) => (coupon.targetIds || []).includes((item.productId || item.product?.id) as string | number),
      );
    } else if (coupon.targetType === COUPON_TARGET_TYPES.COLLECTION) {
      matchingItems = items.filter((item) => {
        const collectionIds = item.product?.collectionIds || [];
        return collectionIds.some((cid) => (coupon.targetIds || []).includes(cid));
      });
    } else {
      return;
    }

    const matchingGross = matchingItems.reduce(
      (sum, item) => sum + item.unitPrice * item.amounts, 0,
    );
    if (matchingGross === 0) return;

    matchingItems.forEach((item) => {
      const itemGross = item.unitPrice * item.amounts;
      const itemDiscount = (itemGross / matchingGross) * coupon.discount;
      map[item.id] = Math.max(0, map[item.id]! - itemDiscount);
    });
  });

  // Normalize: when overlapping coupons over-discount individual items, the
  // per-item clamp (Math.max(0,...)) silently drops the excess, causing
  // sum(map) > targetSum. Scale all effective prices down proportionally so
  // the panel never shows more remaining balance than the customer actually owes.
  const effectiveSum = Object.values(map).reduce((sum, v) => sum + v, 0);
  if (effectiveSum > targetSum + 0.001) {
    const scaleFactor = targetSum / effectiveSum;
    Object.keys(map).forEach((id) => {
      map[id] = map[id]! * scaleFactor;
    });
  }

  return map;
}

const buildInitialState = (transaction?: Transaction): Record<string, unknown> => (
  (transaction as unknown as Record<string, unknown>) || {
    transactionDate: new Date(),
    items: [],
    amount: 0,
    paymentMethod: '',
    notes: '',
    chargeType: CHARGE_TYPE.PERCENTAGE,
    chargeValue: 100, // 100% by default
  }
);

export default function TransactionPanel(props: TransactionPanelProps) {
  const {
    transaction,
    transactions,
    order,
    additionalCharges,
    onSaveTransaction,
  } = props;

  const {
    hideModal,
  } = useContext(FeedbackUIContext)!;

  const {
    values,
    isValid,
    errors,
    errorMessages,
    handleChange,
    handleSubmit,
    addSubmitListener,
  } = useForm({
    initialState: buildInitialState(transaction),
    validations: transactionValidations,
  });

  const formValues = values as unknown as TransactionFormValues;

  const [selectedItemIds, setSelectedItemIds] = useState<Array<string | number>>([]);
  const [selectedAdditionalChargeIds, setSelectedAdditionalChargeIds] = useState<Array<string | number>>([]);
  const [calculatedAmount, setCalculatedAmount] = useState(0);
  const [calculationBreakdown, setCalculationBreakdown] = useState<CalculationBreakdown | null>(null);

  // Memoize itemPaidMap to prevent recalculation on every render
  const itemPaidMap = useMemo(
    () => calcItemPaidAmounts(
      order as unknown as Parameters<typeof calcItemPaidAmounts>[0],
    ),
    [order],
  );

  // Per-item effective price after distributing coupon discounts
  const effectivePriceMap = useMemo(
    () => buildEffectivePriceMap(order.items, order.appliedCoupons),
    [order.items, order.appliedCoupons],
  );

  // Track which additional charges have been used in other transactions
  const usedAdditionalChargeIds = useMemo(() => (transactions || [])
    .filter((t) => t !== transaction) // Exclude current transaction if editing
    .flatMap((t) => (t.items || [])
      .filter((item) => item.type === TRANSACTION_ITEM_TYPES.ADDITIONAL_CHARGE)
      .map((item) => item.value)),
  [transactions, transaction]);

  // Calculate available amount when selection or charge settings change
  useEffect(() => {
    if (selectedItemIds.length === 0 && selectedAdditionalChargeIds.length === 0) {
      setCalculatedAmount(0);
      setCalculationBreakdown(null);
      handleChange(null, { name: 'amount', value: 0 });
      return;
    }

    let totalAmount = 0;
    let uncappedTotal = 0; // Track what the amount would be without capping
    let wasCapped = false;

    // Calculate amount from selected order items
    selectedItemIds.forEach((itemId) => {
      const item = order.items.find((itm) => itm.id === itemId);
      if (!item) return;

      const itemEffectiveTotal = effectivePriceMap[item.id]!;
      const paidAmount = itemPaidMap[itemId] || 0;
      const remaining = Math.max(0, itemEffectiveTotal - paidAmount);

      if (remaining > 0) {
        // Apply charge type based on effective (post-discount) amount, capped at remaining
        let chargeAmount = itemEffectiveTotal;

        if (formValues.chargeType === CHARGE_TYPE.PERCENTAGE) {
          chargeAmount = itemEffectiveTotal * (Number(formValues.chargeValue || 0) / 100);
        } else if (formValues.chargeType === CHARGE_TYPE.FIXED) {
          // For fixed amount, distribute proportionally across selected items
          chargeAmount = itemEffectiveTotal; // Will be adjusted after total calculation
        }

        uncappedTotal += chargeAmount;

        // Cap at remaining balance
        if (chargeAmount > remaining) {
          wasCapped = true;
        }
        chargeAmount = Math.min(chargeAmount, remaining);
        totalAmount += chargeAmount;
      }
    });

    // For fixed amount, recalculate to distribute the fixed value proportionally
    if (formValues.chargeType === CHARGE_TYPE.FIXED && selectedItemIds.length > 0) {
      const totalRemaining = selectedItemIds.reduce<number>((sum, itemId) => {
        const effective = Number(effectivePriceMap[itemId] || 0);
        const paid = Number(itemPaidMap[itemId] || 0);
        return sum + Math.max(0, effective - paid);
      }, 0);

      const fixedValue = Number(formValues.chargeValue || 0);
      uncappedTotal = fixedValue;

      // Cap fixed amount at total remaining
      if (fixedValue > totalRemaining) {
        wasCapped = true;
      }
      totalAmount = Math.min(fixedValue, totalRemaining);
    }

    // Add selected additional charges (full amount, not affected by charge type)
    selectedAdditionalChargeIds.forEach((chargeId) => {
      const charge = additionalCharges.find((ac) => ac._id === chargeId);
      if (charge) {
        totalAmount += Number(charge.price);
        uncappedTotal += Number(charge.price);
      }
    });

    setCalculatedAmount(totalAmount);

    // Store breakdown info for display
    setCalculationBreakdown(wasCapped ? {
      requested: uncappedTotal,
      actual: totalAmount,
      wasCapped: true,
    } : null);

    // Sync the calculated amount with form values for validation
    handleChange(null, { name: 'amount', value: totalAmount });
  }, [
    selectedItemIds,
    selectedAdditionalChargeIds,
    formValues.chargeType,
    formValues.chargeValue,
    itemPaidMap,
    effectivePriceMap,
    // Note: We intentionally exclude handleChange and additionalCharges to prevent infinite loops
    // The calculation logic accesses order.items and additionalCharges from closure
  ]);

  const handleSaveTransaction = (submitValues: Record<string, unknown>, valid: boolean) => {
    if (!valid) {
      return;
    }

    if (selectedItemIds.length === 0 && selectedAdditionalChargeIds.length === 0) {
      return;
    }

    // Build transaction items with calculated amounts
    const transactionItems: TransactionItem[] = [];

    // Add order items
    if (selectedItemIds.length > 0) {
      if (submitValues.chargeType === CHARGE_TYPE.FIXED) {
        // For fixed amount, distribute proportionally based on effective amounts
        const totalOriginal = selectedItemIds.reduce<number>(
          (sum, itemId) => sum + Number(effectivePriceMap[itemId] || 0),
          0,
        );

        const totalRemaining = selectedItemIds.reduce<number>((sum, itemId) => {
          const effective = Number(effectivePriceMap[itemId] || 0);
          const paid = Number(itemPaidMap[itemId] || 0);
          return sum + Math.max(0, effective - paid);
        }, 0);

        const fixedAmount = Math.min(Number(submitValues.chargeValue || 0), totalRemaining);

        selectedItemIds.forEach((itemId) => {
          const itemEffectiveTotal = Number(effectivePriceMap[itemId] || 0);
          const paidAmount = Number(itemPaidMap[itemId] || 0);
          const remaining = Math.max(0, itemEffectiveTotal - paidAmount);

          // Distribute proportionally based on effective amount
          const proportion = totalOriginal > 0 ? itemEffectiveTotal / totalOriginal : 0;
          const itemAmount = Math.min(fixedAmount * proportion, remaining);

          transactionItems.push({
            type: TRANSACTION_ITEM_TYPES.ORDER_ITEM,
            itemId,
            amount: itemAmount,
          });
        });
      } else {
        // For percentage, calculate based on effective amount but cap at remaining
        selectedItemIds.forEach((itemId) => {
          const itemEffectiveTotal = effectivePriceMap[itemId] || 0;
          const paidAmount = itemPaidMap[itemId] || 0;
          const remaining = Math.max(0, itemEffectiveTotal - paidAmount);

          let itemAmount = itemEffectiveTotal;
          if (submitValues.chargeType === CHARGE_TYPE.PERCENTAGE) {
            itemAmount = itemEffectiveTotal * (Number(submitValues.chargeValue || 0) / 100);
          }

          // Cap at remaining
          itemAmount = Math.min(itemAmount, remaining);

          transactionItems.push({
            type: TRANSACTION_ITEM_TYPES.ORDER_ITEM,
            itemId,
            amount: itemAmount,
          });
        });
      }
    }

    // Add additional charges
    selectedAdditionalChargeIds.forEach((chargeId) => {
      const charge = additionalCharges.find((ac) => ac._id === chargeId);
      if (charge) {
        transactionItems.push({
          type: TRANSACTION_ITEM_TYPES.ADDITIONAL_CHARGE,
          value: chargeId,
          amount: Number(charge.price),
        });
      }
    });

    const transactionData = {
      ...submitValues,
      items: transactionItems,
      amount: calculatedAmount,
      chargeType: submitValues.chargeType,
      chargeValue: submitValues.chargeValue,
    };

    hideModal();
    onSaveTransaction(transactionData);
  };
  addSubmitListener(handleSaveTransaction);

  const toggleItem = (itemId: string | number) => {
    setSelectedItemIds((prev) => {
      if (prev.includes(itemId)) {
        return prev.filter((id) => id !== itemId);
      }
      return [...prev, itemId];
    });
  };

  const toggleAdditionalCharge = (chargeId: string | number) => {
    setSelectedAdditionalChargeIds((prev) => {
      if (prev.includes(chargeId)) {
        return prev.filter((id) => id !== chargeId);
      }
      return [...prev, chargeId];
    });
  };

  return (
    <>
      <FormControl sx={{ mb: 5, width: '100%' }}>
        <Stack spacing={2} direction="row" sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
          >
            <DesktopDatePicker
              label="Transaction Date"
              name="transactionDate"
              format={DATE_FORMAT_STRING}
              value={new Date(formValues.transactionDate)}
              slotProps={{
                textField: {
                  size: 'small',
                },
              }}
              sx={{ minWidth: 70 }}
              onChange={(d) => handleChange(null, {
                name: 'transactionDate',
                value: (d as Date).toISOString(),
              })}
            />
          </LocalizationProvider>

          <TextField
            label="Payment Method"
            name="paymentMethod"
            size="small"
            value={formValues.paymentMethod}
            onChange={(e) => handleChange(e, { name: 'paymentMethod', value: e.target.value })}
            sx={{ minWidth: 200 }}
          />
        </Stack>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Select Items to Charge
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {order.items.map((item) => {
              const itemEffectiveTotal = effectivePriceMap[item.id]!;
              const paidAmount = itemPaidMap[item.id] || 0;
              const remaining = Math.max(0, itemEffectiveTotal - paidAmount);
              const fullyPaid = remaining <= 0;
              const selected = selectedItemIds.includes(item.id);

              return (
                <Chip
                  key={item.id}
                  label={`${item.product.name} (${item.color || 'N/A'}/${item.size || 'N/A'}) - ${formatCurrency(itemEffectiveTotal)} total, ${formatCurrency(remaining)} remaining`}
                  disabled={fullyPaid}
                  color={selected ? 'primary' : 'default'}
                  variant={selected ? 'filled' : 'outlined'}
                  icon={selected ? <DoneIcon /> : undefined}
                  onClick={() => !fullyPaid && toggleItem(item.id)}
                  sx={{
                    opacity: fullyPaid ? 0.5 : 1,
                  }}
                />
              );
            })}
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Additional Charges
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {additionalCharges.length === 0 && (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                No additional charges
              </Typography>
            )}
            {additionalCharges.map((charge) => {
              const selected = selectedAdditionalChargeIds.includes(charge._id);
              const alreadyUsed = usedAdditionalChargeIds.includes(charge._id);
              const disabled = alreadyUsed;

              return (
                <Chip
                  key={charge._id}
                  label={`${charge.name} - ${formatCurrency(charge.price)}`}
                  disabled={disabled}
                  color={selected ? 'primary' : 'default'}
                  variant={selected ? 'filled' : 'outlined'}
                  icon={selected || alreadyUsed ? <DoneIcon /> : undefined}
                  onClick={() => !disabled && toggleAdditionalCharge(charge._id)}
                  sx={{
                    opacity: disabled ? 0.5 : 1,
                  }}
                />
              );
            })}
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <FormLabel component="legend">Charge Amount (based on item amount after discounts)</FormLabel>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              display: 'block',
              mb: 1,
            }}
          >
            Percentage/fixed amount is calculated from the effective item price (after coupon discounts), capped at remaining balance. Additional charges are always included at full amount.
          </Typography>
          <RadioGroup
            row
            value={formValues.chargeType}
            onChange={(e) => handleChange(null, { name: 'chargeType', value: e.target.value })}
          >
            <FormControlLabel
              value={CHARGE_TYPE.PERCENTAGE}
              control={<Radio />}
              label="Percentage"
            />
            <FormControlLabel
              value={CHARGE_TYPE.FIXED}
              control={<Radio />}
              label="Fixed Amount"
            />
          </RadioGroup>

          <TextField
            size="small"
            type="number"
            value={formValues.chargeValue}
            onChange={(e) => handleChange(null, { name: 'chargeValue', value: e.target.value })}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    {formValues.chargeType === CHARGE_TYPE.PERCENTAGE ? '%' : '$'}
                  </InputAdornment>
                ),
              },
            }}
            sx={{ width: 150 }}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Tooltip
            title={
              calculationBreakdown && calculationBreakdown.wasCapped
                ? `Requested ${formatCurrency(calculationBreakdown.requested)} but capped at remaining balance`
                : ''
            }
            arrow
          >
            <Typography variant="h6">
              Calculated Amount:
              {' '}
              {formatCurrency(calculatedAmount)}
              {calculationBreakdown && calculationBreakdown.wasCapped && (
                <Typography
                  component="span"
                  variant="caption"
                  sx={{
                    color: 'warning.main',
                    ml: 1,
                  }}
                >
                  (capped from
                  {' '}
                  {formatCurrency(calculationBreakdown.requested)}
                  )
                </Typography>
              )}
            </Typography>
          </Tooltip>
        </Box>

        <TextField
          label="Notes"
          name="notes"
          multiline
          rows={3}
          value={formValues.notes}
          onChange={(e) => handleChange(e, { name: 'notes', value: e.target.value })}
          fullWidth
        />
      </FormControl>
      <Stack
        spacing={2}
        direction="row"
        sx={{ display: 'flex', justifyContent: 'flex-end' }}
      >
        <Alert
          variant="outlined"
          severity="warning"
          sx={{
            visibility: (selectedItemIds.length === 0 && selectedAdditionalChargeIds.length === 0) || errors.amount ? 'visible' : 'hidden',
            width: '470px',
            py: 0,
          }}
        >
          {(selectedItemIds.length === 0 && selectedAdditionalChargeIds.length === 0) ? 'Select at least one item or additional charge' : errorMessages.amount as React.ReactNode}
        </Alert>

        <div>
          <Button
            variant="outlined"
            onClick={hideModal}
          >
            Cancel
          </Button>
        </div>

        <div>
          <Button
            variant="contained"
            disabled={!isValid || (selectedItemIds.length === 0 && selectedAdditionalChargeIds.length === 0)}
            onClick={handleSubmit}
          >
            {transaction ? 'Save' : 'Add'}
          </Button>
        </div>
      </Stack>
    </>
  );
}
